import { usePlayer } from "@empirica/core/player/classic/react";
import React from "react";
import { Button } from "../components/Button";

export function Consent() {
  const player = usePlayer();

  function handleAgree() {
    // mark consent and submit the stage for this player
    player.set("consented", true);
    player.stage.set("submit", true);
    player.set("consentTimestamp", Date.now());
  }

  function handleDecline() {
    // mark declined and submit; experiment team can handle removal
    player.set("consented", false);
    player.stage.set("submit", true);
    player.set("consentTimestamp", Date.now());
  }

  return (
    // Render as a fixed, full-screen overlay so it supersedes any small banners
    <div className="fixed inset-0 bg-white overflow-auto z-50">
      <div className="py-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold mb-4">IUB Informed Consent for Research</h1>
      <h2 className="text-lg font-semibold mb-4">Inter-Agent Contingency Detection in a Minimal Virtual Environment, Protocol #24644</h2>

      <div className="prose max-w-none mb-6">
        <p>
          Welcome to our virtual social interaction experiment, a web-based experiment that examines how people detect and respond to other individuals in a simple virtual environment using only limited sensory information. We aim to see when and how people can tell the difference between a real human partner and automated or non-reactive distractors.
        </p>

        <p>
          You are being asked to participate in a research study. Scientists do research to answer important questions that might help change or improve the way we do things in the future. This document will give you information about the study to help you decide whether you want to participate. Please read this form before agreeing to be in the study.
        </p>

        <p>
          All research is voluntary. You can choose not to take part in this study. If you decide to participate, you can change your mind later and leave the study at any time.
        </p>

        <p>
          The study is being conducted by Gabriel J Severino — IU Cognitive Science Graduate Student, under the supervision of Dr. Ann-Sophie Barwich – IU Cognitive Science and History and Philosophy of Science and Medicine professor and principal investigator, as well as IU Cognitive Science Graduate students: Jag Williams
        </p>

        <h3>If you agree to be in the study, you will do the following things.</h3>
        <p>General session structure (for a 1-hour session):</p>
        <ul>
          <li>Introduction to the task (5 mins)</li>
          <li>Initial training (5 mins)</li>
          <li>Performing experimental task (45 mins including breaks)</li>
          <li>De-briefing and questionnaire (5 mins)</li>
        </ul>

        <h3>What we will measure</h3>
        <ol>
          <li>Demographic information, such as your age and declared gender.</li>
          <li>Information about input device and handedness via questionnaire.</li>
          <li>Behavioral data: time searching, mouse positions, clicks, number of correct detections.</li>
          <li>Subjective information: confidence ratings across rounds and conditions.</li>
          <li>Connection / technical issues encountered.</li>
        </ol>

        <p>
          Before agreeing to participate, please consider the risks and potential benefits of taking part in this study. While we will make every effort to keep your personal information confidential, we cannot guarantee absolute confidentiality. There are also general risks associated with participating in the behavioral tasks. These include mental fatigue, eye strain, or boredom and frustration. Participants may also experience brief discomfort due to prolonged sitting or sustained focus. To minimize these risks, short breaks are provided between each round of the experiment and sessions are at most capped at 2 hours.
        </p>

        <p>
          You are being asked to participate because you responded to our listing on Prolific. Participation in this study is entirely voluntary and you may withdraw at any time. You will receive $12 per hour for completing this study, which is expected to take approximately one hour. All compensation is handled through Prolific, and thus follows Prolific’s guidelines regarding approvals, rejections, and returns.
        </p>

        <p>
          This study involves real-time interaction between two participants. If your paired partner drops out or experiences technical issues, we may attempt to re-pair you with a new partner so you can complete the study. If re-pairing is not feasible, the session may be ended early. In such cases you may still receive partial compensation if you have completed at least half of study.
        </p>

        <h3>Participant Confidentiality</h3>
        <p>
          We will protect your information and make every effort to keep your personal information confidential, but we cannot guarantee absolute confidentiality. No information which could identify you will be shared in publications about this study. Each participant will be assigned an ID number that will be used to identify their data. To maintain confidentiality, the collected data will be stored electronically, separate from any individual-identifying information, on password-protected computers.
        </p>

        <p>
          Information collected for this study may be used for other research studies or shared with other researchers who are conducting their own research studies. Identifying information will be removed before sharing.
        </p>

        <p>
          If you have questions about the study or encounter a problem with the research, contact the researcher, Gabriel J. Severino at gjseveri@iu.edu or via the IU Cognitive Science department at (812) 855-7192. For any questions about your rights as a research participant, contact the IU Human Research Protection Program at 800-696-2949 or irb@iu.edu.
        </p>

        <p className="font-semibold">If you are 18 years of age or older, understand the statements above, and freely consent to participate in the study, click on the "I Agree" button to begin the experiment.</p>
      </div>

        <div className="flex gap-4 mt-8">
          <Button primary={false} handleClick={handleAgree}>I Agree</Button>
          <Button primary={true} handleClick={handleDecline}>I Do Not Agree</Button>
        </div>
      </div>
    </div>
  );
}
